import { BoxClient } from './box-client-v2'
import { notification } from 'antd'
import React, { memo, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { createSelector } from 'reselect'
import { useSelector } from 'react-redux'
import fetchAPI from '../../../lib/utils/fetch-api'
import { ArgsProps } from 'antd/es/notification'
import {
  CheckCircleOutlined,
  CarOutlined,
  CloseCircleOutlined,
  PhoneOutlined,
  UserOutlined,
  HomeOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import {
  isRealtimeSuspended,
  onRealtimeVisibilityChange,
} from '../../../lib/utils/realtime-visibility'

const createSession = createSelector(
  (state: any) => state.session,
  sessionData => sessionData,
)

interface IOpenNotification {
  current_time?: string
  entry_id?: string
  phone?: string
  name?: string
  office_name?: string
  manager_name?: string
  client_id?: number
}

const TopMenu = () => {
  const { sessionData } = useSelector(createSession)
  const navigate = useNavigate()
  const [isOpen, setOpen] = useState<boolean>(false)
  const [clientId, setClientId] = useState<undefined | number>(undefined)
  const callNotificationsRef = useRef<Map<string, IOpenNotification & { onClick: () => void }>>(
    new Map(),
  )

  const getCallNotificationArgs = (
    data: IOpenNotification,
    onClick: () => void,
    title: string | React.ReactNode,
  ): ArgsProps => {
    const description = (
      <div style={{ fontSize: 14, lineHeight: '1.5' }}>
        {data?.office_name && (
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4, fontSize: 15 }}>
            <HomeOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            <span style={{ fontWeight: 600 }}>{data.office_name}</span>
          </div>
        )}
        {data?.manager_name && (
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
            <UserOutlined style={{ marginRight: '8px', color: '#52c41a' }} />
            <span style={{ fontStyle: 'italic' }}>{data.manager_name}</span>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', marginTop: 8 }}>
          <PhoneOutlined style={{ marginRight: '8px', color: '#fa541c' }} />
          <div>
            <div style={{ fontWeight: 600, fontSize: 15 }}>{data?.phone || ''}</div>
            {data?.name && <div style={{ marginTop: 2, fontWeight: 500 }}>{data.name}</div>}
          </div>
        </div>
      </div>
    )
    return {
      message: (
        <div
          style={{
            fontWeight: 'bold',
            fontSize: 16,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <PhoneOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          {title}
        </div>
      ),
      key: data?.entry_id ?? data?.phone,
      description,
      duration: 0,
      style: {
        background: '#d4e7ff',
        userSelect: 'none',
        borderRadius: 3,
        border: '1px solid #9ccfff',
      },
      onClick,
    }
  }

  const openNotification = (
    data: IOpenNotification,
    onClick: () => void,
    options?: { title?: string },
  ) => {
    const title = options?.title ?? (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <div>Входящий звонок</div>
        <div
          style={{
            fontWeight: 'normal',
          }}
        >
          {dayjs(data.current_time).format('HH:mm:ss')}
        </div>
      </div>
    )
    const args = getCallNotificationArgs(data, onClick, title)
    notification.open(args)
  }

  interface IDataNoteProduct {
    parent_id: string | number
    data: {
      attributes: {
        'production-order-number': string
      }
    }
  }

  const openNotificationProduction = async (data: IDataNoteProduct, onClick: () => void) => {
    const payments = await fetchAPI(`/api/web-hooks/add-payment-contract`, {
      method: 'POST',
      body: JSON.stringify({
        targetId: Number(+data.parent_id),
        pay: {
          sum: 0,
          date: '',
        },
      }),
    })

    const hasDebt = +payments.sum - +payments.paymentsSumAll > 0
    const orderId = data.parent_id
    const orderNum = data.data.attributes['production-order-number']
    const message = (
      <>
        Заказ №{orderId} по производственному заказу №{orderNum} готов к отгрузке
        {hasDebt ? (
          <>
            <br />
            <span>по нему есть задолженность!</span>
          </>
        ) : null}
      </>
    )
    const args: ArgsProps = {
      message: message,
      duration: 0,
      style: { background: hasDebt ? '#e38d82' : '#45c915' },
      onClick,
    }
    notification.open(args)
  }

  interface IDeliveryCompleted {
    order_id: number
    parent_id: number
    order_reference: string
    office_id: number
  }

  const openDeliveryCompletedNotification = (data: IDeliveryCompleted, onClick: () => void) => {
    const key = `delivery-${data.order_id}`
    const args: ArgsProps = {
      message: `Доставка по заказу ${data.order_reference} выполнена`,
      description: (
        <div>
          <a
            onClick={() => {
              onClick()
              notification.destroy(key)
            }}
          >
            Перейти к карточке заказа
          </a>
        </div>
      ),
      key,
      duration: 0,
      style: { background: '#c7ffb5' },
      onClick,
      icon: <CheckCircleOutlined style={{ fontSize: '24px', marginTop: '5px' }} />,
    }
    notification.open(args)
  }

  interface IDeliveryScheduled {
    order_reference: string
    delivery_date: string
    drop_window_start: string
    drop_window_end: string
    office_id: number
  }

  const openDeliveryScheduledNotification = (data: IDeliveryScheduled, onClick: () => void) => {
    const key = `delivery-scheduled-${data.order_reference}`
    const args: ArgsProps = {
      message: `Заказ ${data.order_reference} запланирован на доставку на ${data.delivery_date} с ${data.drop_window_start} до ${data.drop_window_end}`,
      description: (
        <div>
          <a
            onClick={() => {
              onClick()
              notification.destroy(key)
            }}
          >
            Перейти к карточке заказа
          </a>
        </div>
      ),
      key,
      duration: 0,
      style: { background: '#c7ffb5' },
      onClick,
      icon: <CarOutlined style={{ fontSize: '24px', marginTop: '5px' }} />,
    }
    notification.open(args)
  }

  interface IDeliveryOnMove {
    order_reference: string
    delivery_date: string
    drop_window_start: string
    drop_window_end: string
    office_id: number
    parent_id: number
  }

  const openDeliveryOnMoveNotification = (data: IDeliveryOnMove, onClick: () => void) => {
    const key = `delivery-on-move-${data.order_reference}`
    const args: ArgsProps = {
      message: `Заказ ${data.order_reference} в доставке ${data.delivery_date}, время доставки с ${data.drop_window_start} до ${data.drop_window_end}`,
      description: (
        <div>
          <a
            onClick={() => {
              onClick()
              notification.destroy(key)
            }}
          >
            Перейти к карточке заказа
          </a>
        </div>
      ),
      key,
      duration: 0,
      style: { background: '#c7ffb5' },
      onClick,
      icon: <CarOutlined style={{ fontSize: '24px', marginTop: '5px' }} />,
    }
    notification.open(args)
  }

  interface IDeliveryCancelled {
    order_reference: string
    office_id: number
    parent_id: number
  }

  const openDeliveryCancelledNotification = (data: IDeliveryCancelled, onClick: () => void) => {
    const key = `delivery-cancelled-${data.order_reference}`
    const args: ArgsProps = {
      message: `Заказ ${data.order_reference} отменен`,
      description: (
        <div>
          <a
            onClick={() => {
              onClick()
              notification.destroy(key)
            }}
          >
            Перейти к карточке заказа
          </a>
        </div>
      ),
      key,
      duration: 0,
      style: { background: '#ffacad' },
      onClick,
      icon: <CloseCircleOutlined style={{ fontSize: '24px', marginTop: '5px' }} />,
    }
    notification.open(args)
  }

  useEffect(() => {
    const url = '/api/web-hooks/sse'
    let eventSource: EventSource | null = null
    let reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null
    let reconnectDelay = 1000
    const maxReconnectDelay = 30000
    let isMounted = true

    const attachListeners = (es: EventSource) => {
      es.addEventListener('manufactureIsReady', async event => {
        const data = JSON.parse(event.data)
        if (
          sessionData.roles.includes(2) &&
          sessionData.organization.id === +data.owner_organization
        ) {
          const onClick = () => {
            navigate(`/order/${data.id}/edit`)
          }
          await openNotificationProduction(data, onClick)
        }
      })

      es.addEventListener('clientCall', event => {
        const data = JSON.parse(event.data) as IOpenNotification
        const onClick = () => {
          setClientId(data?.client_id || 0)
          setTimeout(() => {
            setOpen(true)
          }, 500)
        }
        if (data.entry_id) {
          const map = callNotificationsRef.current
          if (map.size >= 50) {
            const firstKey = map.keys().next().value
            if (firstKey) map.delete(firstKey)
          }
          map.set(data.entry_id, { ...data, onClick })
        }
        openNotification(data, onClick)
      })

      es.addEventListener('clientCallUpdate', event => {
        const update = JSON.parse(event.data) as IOpenNotification
        const { entry_id } = update
        if (!entry_id) return
        const stored = callNotificationsRef.current.get(entry_id)
        const onClick =
          stored?.onClick ??
          (() => {
            setClientId(update?.client_id || 0)
            setTimeout(() => setOpen(true), 500)
          })
        const merged = { ...update, onClick }
        const map = callNotificationsRef.current
        if (map.size >= 50) {
          const firstKey = map.keys().next().value
          if (firstKey) map.delete(firstKey)
        }
        map.set(entry_id, merged)
        const title = (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <div>Входящий звонок</div>
            <div style={{ fontWeight: 'normal' }}>
              {merged.current_time ? dayjs(merged.current_time).format('HH:mm:ss') : null}
            </div>
          </div>
        )
        const args = getCallNotificationArgs(merged, onClick, title)
        notification.open(args)
      })

      es.addEventListener('deliveryCompleted', async event => {
        const data = JSON.parse(event.data)
        if (sessionData?.user?.office.id === +data.office_id) {
          const onClick = () => {
            navigate(`/order/${data.parent_id}`)
          }
          openDeliveryCompletedNotification(data, onClick)
        }
      })

      es.addEventListener('deliveryScheduled', async event => {
        const data = JSON.parse(event.data)
        if (sessionData?.user?.office.id === +data.office_id) {
          const onClick = () => {
            navigate(`/order/${data.parent_id}`)
          }
          openDeliveryScheduledNotification(data, onClick)
        }
      })

      es.addEventListener('deliveryOnMove', async event => {
        const data = JSON.parse(event.data)
        if (sessionData?.user?.office.id === +data.office_id) {
          const onClick = () => {
            navigate(`/order/${data.parent_id}`)
          }
          openDeliveryOnMoveNotification(data, onClick)
        }
      })

      es.addEventListener('deliveryCancelled', async event => {
        const data = JSON.parse(event.data)
        if (sessionData?.user?.office.id === +data.office_id) {
          const onClick = () => {
            navigate(`/order/${data.parent_id}`)
          }
          openDeliveryCancelledNotification(data, onClick)
        }
      })
    }

    const connect = () => {
      if (!isMounted || isRealtimeSuspended()) return
      if (reconnectTimeoutId) {
        clearTimeout(reconnectTimeoutId)
        reconnectTimeoutId = null
      }
      if (eventSource) {
        eventSource.onerror = null
        eventSource.close()
        eventSource = null
      }
      eventSource = new EventSource(url, { withCredentials: true })
      attachListeners(eventSource)

      eventSource.onopen = () => {
        reconnectDelay = 1000
      }

      eventSource.onerror = () => {
        eventSource?.close()
        eventSource = null
        if (!isMounted) return
        if (reconnectTimeoutId) clearTimeout(reconnectTimeoutId)
        reconnectTimeoutId = setTimeout(() => {
          reconnectDelay = Math.min(reconnectDelay * 1.5, maxReconnectDelay)
          connect()
        }, reconnectDelay)
      }
    }

    const suspend = () => {
      if (reconnectTimeoutId) {
        clearTimeout(reconnectTimeoutId)
        reconnectTimeoutId = null
      }
      eventSource?.close()
      eventSource = null
    }

    const removeVisibilityListener = onRealtimeVisibilityChange(() => {
      if (isRealtimeSuspended()) {
        suspend()
      } else {
        connect()
      }
    })

    connect()

    return () => {
      isMounted = false
      removeVisibilityListener()
      suspend()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sessionData is the main trigger; other deps would cause unnecessary reconnects
  }, [sessionData])

  return (
    <BoxClient
      isOpen={isOpen}
      setOpen={() => {
        setOpen(false)
      }}
      id={clientId}
    />
  )
}

export default memo(TopMenu)
